import React, {useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Select} from '@mui/material';
import PropTypes from 'prop-types';
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftInput from "../../../components/SoftInput";
import SoftButton from "../../../components/SoftButton";
import {formatIpAddress} from "../../../utils/formValidations";

const AddEditCCTVConfigDialog = ({open, onClose, cctvConfig, isEdit, onSubmit, branch}) => {
    const initialFormState = {
        configId: '',
        ipAddress: '',
        host: '',
        port: '',
        ddns: '',
        userName: '',
        password: '',
        locationDescription: '',
        status: 'ACTIVE',
        branchId: branch.branchId
    }

    const [formData, setFormData] = useState(initialFormState);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (isEdit && cctvConfig) {
            setFormData({
                configId: cctvConfig.configId || '',
                ipAddress: cctvConfig.ipAddress || '',
                userName: cctvConfig.userName || '',
                password: cctvConfig.password || '',
                port: cctvConfig.port || '',
                host: cctvConfig.host || '',
                ddns: cctvConfig.ddns || '',
                status: cctvConfig.status || '',
                locationDescription: cctvConfig.locationDescription || '',
                branchId: branch.branchId || ''
            });
        } else if (open) {
            setFormData(initialFormState);
        }
    }, [isEdit, cctvConfig, open]);

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit CCTV Config' : 'Add  CCTV Config'}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Location Description
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                name="locationDescription"
                                type="text"
                                placeholder=""
                                value={formData.locationDescription}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        IP Address
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    name="ipAddress"
                                    type="text"
                                    placeholder="Enter IP address (e.g., 192.168.1.1)"
                                    value={formData.ipAddress}
                                    onChange={(e) => {
                                        const formatted = formatIpAddress(e.target.value);
                                        setFormData(prev => ({
                                            ...prev,
                                            ipAddress: formatted
                                        }));
                                    }}
                                />
                                {formData.ipAddress && (
                                    <SoftTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{mt: 0.5, ml: 1}}
                                    >
                                        Format: XXX.XXX.XXX.XXX (auto-formatted)
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Host
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                name="host"
                                type="text"
                                placeholder="Host"
                                value={formData.host}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Port
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                name="port"
                                type="text"
                                placeholder=""
                                value={formData.port}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    DDNS
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                name="ddns"
                                type="text"
                                placeholder=""
                                value={formData.ddns}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    User Name
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                name="userName"
                                type="text"
                                placeholder=""
                                value={formData.userName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Password
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                name="password"
                                type="password"
                                placeholder={isEdit ? "Leave blank to keep current password" : ""}
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Status
                                </SoftTypography>
                            </SoftBox>
                            <Select
                                type="select"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                label="Status"
                            >
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">InActive</MenuItem>
                            </Select>
                        </Grid>
                    </Grid>
                    {error && (
                        <div style={{color: "red", marginTop: "10px"}}>
                            {error}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <SoftButton type="submit" variant="contained" color="primary" disable={error}>
                        {isEdit ? "Update CCTV Config" : "Add CCTV Config"}
                    </SoftButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};

AddEditCCTVConfigDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    cctvConfig: PropTypes.object,
    branch: PropTypes.object,
    isEdit: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
};

export default AddEditCCTVConfigDialog;